/**
 * PaddleIntegrationContent
 *
 * Reusable content component for Paddle Billing integration.
 * Shows connection status and allows connecting/disconnecting Paddle account.
 * Used by IntegrationsPage.
 *
 * @module paddle-connect/components/PaddleIntegrationContent
 */

import { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Collapse,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PaddleConnectionError } from '@modules/paddle-connect/components/PaddleConnectionError';
import { ConnectPaddleForm } from './ConnectPaddleForm';
import { PaddleAccountStatus } from './PaddleAccountStatus';
import { WebhookUrlDisplay } from './WebhookUrlDisplay';
import { usePaddleConnect } from '../hooks/usePaddleConnect';

export interface PaddleIntegrationContentProps {
  /** Project ID to load Paddle config for */
  projectId: string;
  /** Whether to show header title and description */
  showHeader?: boolean;
}

/**
 * PaddleIntegrationContent Component
 *
 * Displays Paddle Billing integration UI:
 * - When not connected: Shows credential form with instructions
 * - When connected: Shows account status with environment and disconnect option
 */
export function PaddleIntegrationContent({
  projectId,
  showHeader = false,
}: PaddleIntegrationContentProps) {
  const theme = useTheme();
  const { status, loading, error, refresh } = usePaddleConnect(projectId);

  // State for showing connect form for a specific environment
  const [connectingEnvironment, setConnectingEnvironment] = useState<'sandbox' | 'production' | null>(null);

  const handleConnected = useCallback(() => {
    setConnectingEnvironment(null); // Hide form after successful connection
    refresh();
  }, [refresh]);

  const handleDisconnected = useCallback(() => {
    refresh();
  }, [refresh]);

  const handleError = useCallback((errorMessage: string) => {
    console.error('Paddle connection error:', errorMessage);
  }, []);

  const handleConnectEnvironment = useCallback((env: 'sandbox' | 'production') => {
    setConnectingEnvironment(env);
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const isConnected = status?.connected;
  if (!status) return <PaddleConnectionError error={error} hasVerifiedStatus={false} onRetry={refresh} />;

  return (
    <Box>
      {error && <PaddleConnectionError error={error} hasVerifiedStatus onRetry={refresh} />}
      {/* Optional Header */}
      {showHeader && (
        <>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Paddle Integration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect your Paddle account to accept payments in your onboarding funnels.
          </Typography>
        </>
      )}

      {/* Connection Status */}
      {isConnected ? (
        <>
          <PaddleAccountStatus
            key={projectId}
            projectId={projectId}
            showDisconnect
            onDisconnect={handleDisconnected}
            onConnectEnvironment={handleConnectEnvironment}
          />

          {/* Form to connect additional environment */}
          <Collapse in={connectingEnvironment !== null}>
            <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Connect {connectingEnvironment === 'production' ? 'Production' : 'Sandbox'} Environment
              </Typography>
              <ConnectPaddleForm
                key={projectId}
                projectId={projectId}
                onConnected={handleConnected}
                onError={handleError}
                defaultEnvironment={connectingEnvironment || undefined}
              />
            </Paper>
          </Collapse>

          {/* Webhook URL - critical for receiving Paddle events */}
          <WebhookUrlDisplay projectId={projectId} showInstructions />
        </>
      ) : (
        /* Not Connected State */
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            borderColor: theme.palette.divider,
            borderStyle: 'dashed',
          }}
        >
          <Box sx={{ mb: 3 }}>
            {/* Paddle Logo */}
            <svg
              width="60"
              height="60"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ opacity: 0.7 }}
            >
              <rect width="32" height="32" rx="6" fill="#FFDE59" />
              <path
                d="M8 10.5C8 9.67157 8.67157 9 9.5 9H22.5C23.3284 9 24 9.67157 24 10.5V21.5C24 22.3284 23.3284 23 22.5 23H9.5C8.67157 23 8 22.3284 8 21.5V10.5Z"
                fill="#1B1B1B"
              />
              <path
                d="M12 13H14V19H12V13ZM16 13H18V19H16V13ZM20 13V19H18V13H20Z"
                fill="#FFDE59"
              />
            </svg>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <ConnectPaddleForm
              key={projectId}
              projectId={projectId}
              onConnected={handleConnected}
              onError={handleError}
            />
          </Box>

          {/* Show webhook URL even before connecting so users can set it up */}
          <WebhookUrlDisplay projectId={projectId} showInstructions compact />
        </Paper>
      )}
    </Box>
  );
}

export default PaddleIntegrationContent;
